import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";


const PackageDetails = () => {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [packageData, setPackageData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [activeTab, setActiveTab] = useState("itinerary");
 


  const placeholderImage = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80";

  useEffect(() => {
    const fetchPackageDetails = async () => {
      if (!id) return;
      try {
        setFetching(true);
        const res = await apiService.get(`${config.GETCOMPANYPACKAGEBYID}/${id}`);
        if (res) {
          setPackageData(res);
        }
      } catch (error) {
        console.error("Error fetching package details:", error);
      } finally {
        setFetching(false);
      }
    };

    if (user && !loading) {
      fetchPackageDetails();
    }
  }, [id, user, loading]);

  // Helper to extract and map all flights from the itinerary day plans
  const getAllFlightsFromItinerary = () => {
    if (!packageData?.itinerary) return [];
    const flights = [];
    packageData.itinerary.forEach((dayPlan) => {
      if (dayPlan.flights && dayPlan.flights.length > 0) {
        dayPlan.flights.forEach((flight) => {
          flights.push({
            day: dayPlan.day,
            ...flight,
          });
        });
      }
    });
    return flights;
  };

  // Helper to lookup dynamic hotel pricing from itinerary entries by hotel ID or hotel name
  const getHotelPriceFromItinerary = (hotelId, hotelName) => {
    if (!packageData?.itinerary) return null;
    let price = null;
    packageData.itinerary.forEach((dayPlan) => {
      if (dayPlan.hotels && dayPlan.hotels.length > 0) {
        dayPlan.hotels.forEach((hotel) => {
          if (
            (hotelId && hotel.packageHotelBookingId === hotelId) ||
            hotel.hotelName === hotelName
          ) {
            price = hotel.price;
          }
        });
      }
    });
    return price;
  };
  const compileDestinationsFromItinerary = () => {
    if (!packageData?.itinerary || packageData.itinerary.length === 0) return [];
   
    const compiledDestinations = [];
    const cityMap = new Map();
    let sequenceNum = 1;

    packageData.itinerary.forEach(dayPlan => {
      if (dayPlan.hotels && dayPlan.hotels.length > 0) {
        dayPlan.hotels.forEach(hotel => {
          const cityName = hotel.city || dayPlan.location || "Unknown";
          const normalizedCity = cityName.trim().toLowerCase();
         
          if (!cityMap.has(normalizedCity)) {
            const placeObj = {
              sequence: sequenceNum++,
              cityName: cityName,
              nights: parseInt(hotel.nights) || 1,
              hotels: [
                {
                  hotelId: hotel.packageHotelBookingId,
                  hotelName: hotel.hotelName || "Suggested Hotel",
                  roomType: hotel.roomType || "",
                  mealPlan: hotel.mealPlan || "",
                  starRating: parseInt(hotel.starRating) || 0
                }
              ]
            };
            cityMap.set(normalizedCity, placeObj);
          } else {
            const existingPlace = cityMap.get(normalizedCity);
            existingPlace.nights += (parseInt(hotel.nights) || 0);
           
            const hotelExists = existingPlace.hotels.some(h => h.hotelName === hotel.hotelName);
            if (!hotelExists) {
              existingPlace.hotels.push({
                hotelId: hotel.packageHotelBookingId,
                hotelName: hotel.hotelName || "Suggested Hotel",
                roomType: hotel.roomType || "",
                mealPlan: hotel.mealPlan || "",
                starRating: parseInt(hotel.starRating) || 0
              });
            }
          }
        });
      }
    });

    const placesList = Array.from(cityMap.values());
    if (placesList.length > 0) {
      const primaryCountry = packageData.destinationsCover?.split(',')[0]?.trim() || "Destination";
      compiledDestinations.push({
        countryCode: "IN",
        countryName: primaryCountry,
        places: placesList
      });
    }

    return compiledDestinations;
  };

  if (loading || fetching) return <p className="p-4">Loading package details...</p>;
  if (!user) {
    navigate("/");
    return null;
  }
  if (!packageData) return <div className="p-4 text-center">Package details not found.</div>;

  const aggregatedFlights = getAllFlightsFromItinerary();

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
           
            {/* Header Hero Section */}
            <div className="row">
              <div className="col-12">
                <div className="card border-0 overflow-hidden shadow-sm mb-4">
                  <div className="position-relative" style={{ height: "350px" }}>
                    <img
                      src={packageData.imageSrc || placeholderImage}
                      alt={packageData.title}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                    <div className="position-absolute bottom-0 start-0 w-100 p-4 bg-gradient-dark text-white d-flex flex-column justify-content-end" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)", height: "100%" }}>
                      <div className="d-flex gap-2 mb-2">
                        <span className="badge bg-primary text-uppercase px-3 py-2">{packageData.travelType}</span>
                      </div>
                      <h1 className="text-white mb-2">{packageData.title}</h1>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-4">
              {/* Left Column: Core content segments */}
              <div className="col-lg-8">
               
                {/* Brief description and Destination Covers */}
                <div className="card p-4 shadow-sm mb-4 border-0">
                  <h4 className="card-title mb-3">Overview</h4>
                  <p className="text-muted fs-15">{packageData.description}</p>
                  <div className="p-3 bg-light rounded mt-3">
                    {packageData.destinations && packageData.destinations.length > 0 && (
                      <div className="mb-3">
                        <span className="text-muted fs-11 text-uppercase fw-semibold d-block">Countries:</span>
                        <div className="d-flex flex-wrap gap-2 mt-1">
                          {packageData.destinations.map((dest, dIdx) => (
                            <span key={dIdx} className="badge bg-soft-primary text-primary px-2.5 py-1.5 fs-12 fw-medium">
                              <i className="ri-earth-line me-1 align-bottom"></i> {dest.countryName}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <span className="text-muted fs-11 text-uppercase fw-semibold d-block">Destinations Covered:</span>
                    <h5 className="mb-0 fs-14 mt-1">
                      <i className="ri-map-pin-line text-danger me-2"></i>
                      {packageData.destinationsCover}
                    </h5>
                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-header border-bottom bg-transparent p-0">
                    <ul className="nav nav-tabs nav-tabs-custom nav-success nav-justified border-bottom-0 mb-0">
                      <li className="nav-item">
                        <button
                          className={`nav-link py-3 ${activeTab === "itinerary" ? "active text-success border-success" : ""}`}
                          onClick={() => setActiveTab("itinerary")}
                        >
                          <i className="ri-road-map-line me-1 align-bottom"></i> Day-Wise Itinerary
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link py-3 ${activeTab === "flights" ? "active text-success border-success" : ""}`}
                          onClick={() => setActiveTab("flights")}
                        >
                          <i className="ri-plane-line me-1 align-bottom"></i> Flights Details
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link py-3 ${activeTab === "destinations" ? "active text-success border-success" : ""}`}
                          onClick={() => setActiveTab("destinations")}
                        >
                          <i className="ri-global-line me-1 align-bottom"></i> Route & Hotels
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          className={`nav-link py-3 ${activeTab === "inclusions" ? "active text-success border-success" : ""}`}
                          onClick={() => setActiveTab("inclusions")}
                        >
                          <i className="ri-checkbox-circle-line me-1 align-bottom"></i> Inclusions & Exclusions
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="card-body p-4">
                    {/* Tab 1: Itinerary */}
                    {activeTab === "itinerary" && (
                      <div className="timeline-container">
                        {packageData.itinerary && packageData.itinerary.map((dayPlan, index) => (
                          <div className="pb-4 mb-4 border-bottom last-border-0" key={index}>
                            <div className="d-flex align-items-center mb-2">
                              <span className="badge bg-soft-success text-success fs-13 px-3 py-2 me-3">Day {dayPlan.day}</span>
                              <h5 className="mb-0">{dayPlan.title}</h5>
                            </div>
                            <p className="text-muted fs-14 ps-1">{dayPlan.description}</p>
                           
                            {dayPlan.meals && (
                              <div className="mb-3 ps-1">
                                <span className="badge bg-light text-dark border"><i className="ri-restaurant-line me-1"></i> Meals: {dayPlan.meals}</span>
                              </div>
                            )}

                            {/* Flights Inside Itinerary */}
                            {dayPlan.flights && dayPlan.flights.length > 0 && dayPlan.flights.map((flight, fIdx) => (
                              <div className="bg-light p-3 rounded mb-2 border-start border-primary border-3" key={fIdx}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <h6 className="fs-13 text-primary mb-0"><i className="ri-plane-line me-1"></i> Flight Information</h6>
                                  {flight.price > 0 && (
                                    <span className="badge bg-soft-primary text-primary">{packageData.currency} {flight.price}</span>
                                  )}
                                </div>
                                <p className="mb-0 fs-12 text-muted">
                                  <strong>{flight.airline}</strong> ({flight.flightNumber}) &bull; Class: {flight.cabinClass} <br/>
                                  Route: {flight.departureCity} &rarr; {flight.arrivalCity}
                                </p>
                              </div>
                            ))}

                            {/* Hotels Inside Itinerary */}
                            {dayPlan.hotels && dayPlan.hotels.length > 0 && dayPlan.hotels.map((hotel, hIdx) => (
                              <div className="bg-light p-3 rounded mb-2 border-start border-warning border-3" key={hIdx}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <h6 className="fs-13 text-warning mb-0"><i className="ri-hotel-line me-1"></i> Accommodations Details</h6>
                                  {hotel.price > 0 && (
                                    <span className="badge bg-soft-warning text-warning">{packageData.currency} {hotel.price}</span>
                                  )}
                                </div>
                                <p className="mb-0 fs-12 text-muted">
                                  <strong>{hotel.hotelName}</strong> ({hotel.starRating}★) &bull; Room: {hotel.roomType} <br/>
                                  Nights: {hotel.nights} night(s) &bull; Meal Plan: {hotel.mealPlan}
                                </p>
                              </div>
                            ))}

                            {/* Sightseeing Inside Itinerary */}
                            {dayPlan.sightseeing && dayPlan.sightseeing.length > 0 && dayPlan.sightseeing.map((sight, sIdx) => (
                              <div className="bg-light p-3 rounded mb-2 border-start border-success border-3" key={sIdx}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <h6 className="fs-13 text-success mb-0"><i className="ri-landscape-line me-1"></i> Guided Tour / Sightseeing</h6>
                                  {sight.price > 0 && (
                                    <span className="badge bg-soft-success text-success">{packageData.currency} {sight.price}</span>
                                  )}
                                </div>
                                <p className="mb-0 fs-12 text-muted">
                                  <strong>{sight.sightseeingName}</strong> &bull; Duration: {sight.duration} ({sight.type}) <br/>
                                  {sight.description}
                                </p>
                              </div>
                            ))}

                            {/* Transfers Inside Itinerary */}
                            {dayPlan.transfers && dayPlan.transfers.length > 0 && dayPlan.transfers.map((trans, tIdx) => (
                              <div className="bg-light p-3 rounded mb-2 border-start border-info border-3" key={tIdx}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <h6 className="fs-13 text-info mb-0"><i className="ri-car-line me-1"></i> Transfer details</h6>
                                  {trans.price > 0 && (
                                    <span className="badge bg-soft-info text-info">{packageData.currency} {trans.price}</span>
                                  )}
                                </div>
                                <p className="mb-0 fs-12 text-muted">
                                 <strong>{trans.type ? trans.type.replace("_", " ") : "Private Transfer"}</strong> &bull; Vehicle: {trans.vehicleDetails || "Private Vehicle"} <br/>
                                  Route: {trans.fromLocation} &rarr; {trans.toLocation}
                                </p>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Tab 2: Flights */}
                    {activeTab === "flights" && (
                      <div>
                        <div className="bg-soft-primary p-3 rounded mb-4 d-flex justify-content-between align-items-center">
                          <span className="fw-semibold text-primary fs-14">
                            <i className="ri-plane-line me-2"></i>Aggregated Flight Route Summary
                          </span>
                          <span className="badge bg-primary fs-13 py-1 px-3">
                            Total Flight Cost: {packageData.currency} {packageData.flightPrice?.toLocaleString() || "0"}
                          </span>
                        </div>
                        <div className="table-responsive">
                          <table className="table align-middle table-nowrap mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Day</th>
                                <th>Airline Info</th>
                                <th>Flight Number</th>
                                <th>Class & Bag</th>
                                <th>Route (From &rarr; To)</th>
                                <th>Booking Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {aggregatedFlights.length > 0 ? (
                                aggregatedFlights.map((flight, idx) => (
                                  <tr key={idx}>
                                    <td>
                                      <span className="badge bg-soft-success text-success px-2 py-1">Day {flight.day}</span>
                                    </td>
                                    <td className="fw-medium">{flight.airline}</td>
                                    <td><code>{flight.flightNumber}</code></td>
                                    <td>
                                      <span className="text-dark fs-12 d-block">{flight.cabinClass}</span>
                                      <span className="text-muted fs-11 d-block">Baggage: {flight.baggage || "N/A"}</span>
                                    </td>
                                    <td>
                                      <div className="fw-semibold fs-12">
                                        {flight.departureCity} &rarr; {flight.arrivalCity}
                                      </div>
                                      <span className="text-muted fs-11">
                                        {flight.departureDateTime && new Date(flight.departureDateTime).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fw-semibold text-primary">
                                        {packageData.currency} {flight.price ? flight.price.toLocaleString() : "Included"}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="6" className="text-center py-4 text-muted">
                                    No flight bookings registered for this package.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Tab 3: Destinations */}
                    {activeTab === "destinations" && (
                      <div>
                        <div className="bg-soft-warning p-3 rounded mb-4 d-flex justify-content-between align-items-center">
                          <span className="fw-semibold text-warning fs-14">
                            <i className="ri-hotel-line me-2"></i>Destination & Accommodation Summary
                          </span>
                          <span className="badge bg-warning text-dark fs-13 py-1 px-3">
                            Total Hotel Cost: {packageData.currency} {packageData.hotelPrice?.toLocaleString() || "0"}
                          </span>
                        </div>
                        {((packageData.destinations && packageData.destinations.length > 0)
                          ? packageData.destinations
                          : compileDestinationsFromItinerary()).map((dest, dIdx) => (
                          <div className="mb-4" key={dIdx}>
                            <h5 className="text-primary mb-3"><i className="ri-global-line me-2"></i>{dest.countryName}</h5>
                            <div className="table-responsive">
                              <table className="table align-middle table-nowrap mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Sequence</th>
                                    <th>City Name</th>
                                    <th>Nights</th>
                                    <th>Suggested Hotel Details</th>
                                    <th>Price Estimate</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dest.places && dest.places.map((place, pIdx) => (
                                    <tr key={pIdx}>
                                      <td>{place.sequence}</td>
                                      <td className="fw-medium">{place.cityName}</td>
                                      <td>{place.nights} night(s)</td>
                                      <td>
                                        {place.hotels && place.hotels.map((h, hIdx) => (
                                          <div key={hIdx}>
                                            <strong>{h.hotelName}</strong> ({h.starRating}★) <br/>
                                            <span className="text-muted fs-12">Room: {h.roomType} &bull; Meal: {h.mealPlan}</span>
                                          </div>
                                        ))}
                                      </td>
                                      <td>
                                        {place.hotels && place.hotels.map((h, hIdx) => {
                                          const matchedPrice = getHotelPriceFromItinerary(h.hotelId, h.hotelName);
                                          return (
                                            <div key={hIdx} className="fw-semibold text-dark">
                                              {matchedPrice > 0 ? (
                                                <span>{packageData.currency} {matchedPrice.toLocaleString()}</span>
                                              ) : (
                                                <span className="text-muted fs-12">Included</span>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Tab 4: Inclusions & Exclusions */}
                    {activeTab === "inclusions" && (
                      <div className="row">
                        <div className="col-md-6 border-end">
                          <h5 className="text-success mb-3"><i className="ri-checkbox-circle-fill me-2 align-bottom"></i> Inclusions</h5>
                          <ul className="list-unstyled ps-0">
                            {packageData.inclusions && packageData.inclusions.map((inc, index) => (
                              <li className="d-flex align-items-start mb-2 fs-14 text-muted" key={index}>
                                <i className="ri-check-line text-success me-2 mt-1 fs-15"></i> {inc}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="col-md-6 ps-md-4">
                          <h5 className="text-danger mb-3"><i className="ri-close-circle-fill me-2 align-bottom"></i> Exclusions</h5>
                          <ul className="list-unstyled ps-0">
                            {packageData.exclusions && packageData.exclusions.map((exc, index) => (
                              <li className="d-flex align-items-start mb-2 fs-14 text-muted" key={index}>
                                <i className="ri-close-line text-danger me-2 mt-1 fs-15"></i> {exc}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Terms and Conditions Accordion */}
                <div className="card p-4 shadow-sm border-0 mb-4">
                  <h4 className="card-title mb-3">Policies, Terms & Conditions</h4>
                  {packageData.terms && packageData.terms.map((term, index) => (
                    <div className="mb-3 border-bottom pb-3 last-border-0" key={index}>
                      <h6 className="text-dark fw-semibold mb-1">{term.title}</h6>
                      <p className="text-muted mb-0 fs-13">{term.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column: Pricing details */}
              <div className="col-lg-4">
                <div className="card border-0 p-4 shadow-sm position-sticky" style={{ top: "90px" }}>
                  <h4 className="card-title mb-3">Sale Summary</h4>
                  <div className="bg-soft-success p-3 rounded text-center mb-4">
                    <span className="text-muted fs-11 text-uppercase fw-semibold d-block">Starting Package Price From</span>
                    <h2 className="text-success mb-0 fw-bold">
                      {packageData.currency || "USD"} {packageData.salePrice ? packageData.salePrice.toLocaleString() : "N/A"}
                    </h2>
                  </div>

                  {/* Pricing Breakdown */}
                  <div className="mb-4">
                    <h6 className="text-dark fw-semibold mb-3 border-bottom pb-2">Cost Components</h6>
                    <div className="d-flex justify-content-between mb-2 fs-13">
                      <span className="text-muted">Flight Cost</span>
                      <span className="fw-semibold text-dark">
                        {packageData.currency} {packageData.flightPrice?.toLocaleString() || "0"}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 fs-13">
                      <span className="text-muted">Hotel Cost</span>
                      <span className="fw-semibold text-dark">
                        {packageData.currency} {packageData.hotelPrice?.toLocaleString() || "0"}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 fs-13">
                      <span className="text-muted">Sightseeing Cost</span>
                      <span className="fw-semibold text-dark">
                        {packageData.currency} {packageData.sightSeeingPrice?.toLocaleString() || "0"}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2 fs-13">
                      <span className="text-muted">Transfers Cost</span>
                      <span className="fw-semibold text-dark">
                        {packageData.currency} {packageData.transferPrice?.toLocaleString() || "0"}
                      </span>
                    </div>
                    {packageData.otherPrice > 0 && (
                      <div className="d-flex justify-content-between mb-2 fs-13">
                        <span className="text-muted">Other Cost</span>
                        <span className="fw-semibold text-dark">
                          {packageData.currency} {packageData.otherPrice?.toLocaleString() || "0"}
                        </span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between border-top pt-2 mt-2 fs-14 fw-bold">
                      <span className="text-dark">Total Net</span>
                      <span className="text-primary">
                        {packageData.currency} {packageData.purchasePrice?.toLocaleString() || "0"}
                      </span>
                    </div>
                  </div>

                  {/* Occupancy Pricing Matrix */}
                  {packageData.occupancyPricing && packageData.occupancyPricing.landOnly && (
                    <div className="mb-4">
                      <h6 className="text-dark fw-semibold mb-3 border-bottom pb-2">Occupancy Rates</h6>
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 fs-14">
                          <span>Double Sharing Price</span>
                          <span className="fw-semibold text-dark">
                            {packageData.occupancyPricing.currency || "USD"} {packageData.occupancyPricing.landOnly.double.toLocaleString()}
                          </span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 fs-14">
                          <span>Triple Sharing Price</span>
                          <span className="fw-semibold text-dark">
                            {packageData.occupancyPricing.currency || "USD"} {packageData.occupancyPricing.landOnly.triple.toLocaleString()}
                          </span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 fs-14">
                          <span>Single Room Price</span>
                          <span className="fw-semibold text-dark">
                            {packageData.occupancyPricing.currency || "USD"} {packageData.occupancyPricing.landOnly.single.toLocaleString()}
                          </span>
                        </li>
                        {packageData.occupancyPricing.landOnly.childWithBed && (
                          <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 fs-14">
                            <span>Child With Bed</span>
                            <span className="fw-semibold text-dark">
                              {packageData.occupancyPricing.currency || "USD"} {packageData.occupancyPricing.landOnly.childWithBed.toLocaleString()}
                            </span>
                          </li>
                        )}
                        {packageData.occupancyPricing.landOnly.childWithoutBed && (
                          <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-2 fs-14">
                            <span>Child Without Bed</span>
                            <span className="fw-semibold text-dark">
                              {packageData.occupancyPricing.currency || "USD"} {packageData.occupancyPricing.landOnly.childWithoutBed.toLocaleString()}
                            </span>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                 
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

    
    </div>
  );
};

export default PackageDetails;   